import type { ComponentType } from 'react';
import { Link } from 'wouter';
import { ArtworkDetail } from './components/ArtworkDetail';
import { Header } from './components/Header';
import { TruthBeTold } from './components/TruthBeTold';

type ArchiveImage = {
  src: string;
  alt: string;
  width: number;
  height: number;
};

export interface ArchiveProject {
  slug: string;
  title: string;
  year?: string;
  hasBottomSpacer?: boolean;
  Component: ComponentType;
}

const goodLuckOutThereImages: ArchiveImage[] = [];
const goodLuckOutThereText: string[] = [];

function ArchiveBackLink({ href }: { href: string }) {
  return (
    <div
      className="fixed left-4 right-4 z-30 text-left text-base font-light text-black md:text-xl"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
    >
      <Link
        href={href}
        className="inline-flex min-h-11 items-center pr-4 transition-opacity duration-200 ease-in-out hover:opacity-70 md:min-h-0"
      >
        Back
      </Link>
    </div>
  );
}

function GoodLuckOutThereArchive() {
  return (
    <section className="flex w-full flex-1 flex-col items-center justify-center gap-8 px-4 pb-24 pt-0">
      {goodLuckOutThereImages.length > 0 ? (
        <div className="flex max-w-full flex-col items-center justify-center gap-8 md:flex-row">
          {goodLuckOutThereImages.map((image) => (
            <img
              key={image.src}
              src={image.src}
              alt={image.alt}
              width={image.width}
              height={image.height}
              loading="eager"
              decoding="async"
              className="block h-auto w-[min(82vw,640px)] object-contain md:max-h-[min(58vh,680px)] md:w-auto xl:max-h-[720px]"
            />
          ))}
        </div>
      ) : (
        <div
          className="aspect-[4/5] w-[min(82vw,420px)] border border-black/15"
          aria-hidden="true"
        />
      )}

      {goodLuckOutThereText.length > 0 && (
        <div className="w-full max-w-[560px] text-left text-base font-light leading-relaxed text-black md:text-xl">
          {goodLuckOutThereText.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      )}
    </section>
  );
}

export const asTheDayBurnsArchive: ArchiveProject = {
  slug: 'as-the-day-burns',
  title: 'As the Day Burns',
  year: '2023-2026',
  hasBottomSpacer: true,
  Component: ArtworkDetail,
};

export const truthBeToldArchive: ArchiveProject = {
  slug: 'truth-be-told',
  title: 'Truth Be Told',
  Component: TruthBeTold,
};

export const goodLuckOutThereArchive: ArchiveProject = {
  slug: 'good-luck-out-there',
  title: 'good luck out there',
  Component: GoodLuckOutThereArchive,
};

export const archiveProjects: ArchiveProject[] = [
  asTheDayBurnsArchive,
  truthBeToldArchive,
];

export function getArchiveProjectBySlug(
  slug: string | undefined,
): ArchiveProject | undefined {
  return archiveProjects.find((project) => project.slug === slug);
}

export function ArchiveIndex() {
  return (
    <div className="flex min-h-dvh flex-col">
      <Header title="Archive" />
      <main className="relative flex min-h-0 w-full flex-1 items-center justify-center px-4 pb-24">
        <nav
          className="flex flex-col items-center gap-2 text-center text-base font-light text-black md:text-xl"
          aria-label="Archive"
        >
          {archiveProjects.map((project) => (
            <Link
              key={project.slug}
              href={`/archive/${project.slug}`}
              className="transition-opacity duration-200 ease-in-out hover:opacity-70"
            >
              {project.title}
            </Link>
          ))}
        </nav>
      </main>
      <ArchiveBackLink href="/" />
    </div>
  );
}

export function ArchiveProjectPage({ project }: { project: ArchiveProject }) {
  const { Component } = project;

  return (
    <div className="flex min-h-dvh flex-col">
      <Header
        title={
          <>
            <span className="hidden sm:inline">
              <span className="font-light opacity-50">Archive</span>
              {' / '}
            </span>
            {project.title}
          </>
        }
      />
      <main className="relative flex min-h-0 w-full flex-1 items-center justify-center p-0">
        <Component />
      </main>
      {project.hasBottomSpacer && (
        <div aria-hidden="true" className="h-[156px] shrink-0" />
      )}
      <ArchiveBackLink href="/archive" />
    </div>
  );
}
